import React, { useContext, useEffect, useRef, useState } from 'react'
import { GetRef, InputRef, Select, TableProps } from 'antd'
import { Button, Form, Input, Popconfirm, Table } from 'antd'

type FormInstance<T> = GetRef<typeof Form<T>>

const EditableContext = React.createContext<FormInstance<any> | null>(null)

interface EditableRowProps {
  index: number
}

const EditableRow: React.FC<EditableRowProps> = ({ index, ...props }) => {
  const [form] = Form.useForm()

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  )
}

interface EditableCellProps {
  title: React.ReactNode
  editable: boolean
  dataIndex: any
  record: any
  handleSave: (record: any) => void
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false)
  const inputRef = useRef<InputRef>(null)
  const form = useContext(EditableContext)!

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
    }
  }, [editing])

  const toggleEdit = () => {
    //

    setEditing(!editing)

    form.setFieldsValue({ [dataIndex]: record[dataIndex] })
  }

  const save = async () => {
    try {
      const values = await form.validateFields()

      toggleEdit()

      handleSave({ ...record, ...values })
    } catch (errInfo) {
      //
    }
  }

  let childNode = children

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        {dataIndex === 'type' ? (
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder="DUP or DEL"
          />
        ) : dataIndex === 'genotype' ? (
          <Input
            ref={inputRef}
            onPressEnter={save}
            onBlur={save}
            placeholder="0/1 or 1/1"
          />
        ) : (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        )}
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingInlineEnd: 24 }}
        onClick={() => {
          //
        }}
        key={dataIndex}
      >
        {children}
      </div>
    )
  }

  return (
    <td
      {...restProps}
      onClick={() => {
        if (editable && !record.items) {
          toggleEdit()
        }
      }}
    >
      {childNode}
    </td>
  )
}

type ColumnTypes = Exclude<TableProps<any>['columns'], undefined>

const EditAndAddTable = React.forwardRef(
  (
    props: {
      columns: any[]
      rows: any[]
      expandColumns: any[]
      analysisInfo: any
    },
    ref
  ) => {
    const [dataSource, setDataSource] = useState<any[]>([])

    const [count, setCount] = useState(2)

    const handleDelete = (row: any) => {
      const newData = dataSource.filter((item) => item.key !== row.key)

      //

      setDataSource(newData)
    }

    const expandedRowRender = (record: any) => {
      //

      return (
        <Table<any>
          columns={props.expandColumns}
          dataSource={record.items}
          pagination={false}
        />
      )
    }

    const handleAdd = () => {
      const newData: any = {
        key: count,
        chrom: 'X'
      }

      //

      setDataSource([...dataSource, newData])

      setCount(count + 1)
    }

    const handleSave = (row: any) => {
      const newData = [...dataSource, row]

      setDataSource(newData)
    }

    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell
      }
    }

    const columns = React.useMemo(() => {
      return props.columns.map((col) => {
        if (!col.editable) {
          return col
        }

        return {
          ...col,
          onCell: (record: any) => ({
            record,
            editable: col.editable,
            dataIndex: col.dataIndex,
            title: col.title,
            handleSave
          })
        }
      })
    }, [props.columns])

    React.useEffect(() => {
      setDataSource(props.rows)
    }, [props.rows])

    React.useImperativeHandle(ref, () => ({
      handleDelete: handleDelete,
      dataSource: dataSource
    }))

    return (
      <div>
        {!props.analysisInfo.pipelineVersion && (
          <Button
            onClick={handleAdd}
            type="primary"
            style={{ marginBottom: 16 }}
          >
            Add CNV
          </Button>
        )}

        <Table<any>
          components={components}
          rowClassName={() => 'editable-row'}
          bordered
          dataSource={dataSource}
          columns={columns as ColumnTypes}
          expandable={{ expandedRowRender, defaultExpandedRowKeys: ['0'] }}
        />
      </div>
    )
  }
)

export default EditAndAddTable
